/**
 * `<MemberMapView/>` — the MEMBER experience of the task Map (issue #761).
 *
 * Phase 2. A non-lead member of a tasks team gets a calm, oriented view of
 * the plan rather than the full two-way editor (#759/#760, which leads keep):
 *
 *   - **My work** (B5.1) — the member's OWN slice: the nodes assigned to them,
 *     with state + "in: <parent>" context + a single focal callout. The empty
 *     case is B8.1.
 *   - **Full map** (B5.2) — the WHOLE tree (everyone sees it — §A8), with the
 *     member's parts highlighted and the rest as read-only context.
 *   - **Members** — the shared members surface (Phase-0 placeholder for now).
 *
 * The whole tree is still ONE data source (`useTaskNodes`); these tabs are
 * different lenses on it, not different scopes. Acting on a node happens in the
 * detail drawer (B5.3), whose controls are gated by `canAuthor` (§A5 recursive
 * ownership) so the UI never offers an action the API would 403.
 *
 * Role-branching is centralised: `MapEditor` decides lead-vs-member up front
 * and mounts this for members; this component does not itself re-check role.
 * Segmentation: taskmap-local imports only; no scheduler coupling.
 */

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TeamZoneContext } from "./theme/TeamZoneContext";

import { useTeam } from "../api/useTeam";
import { useTeamFeature } from "../api/useTeamFeature";
import { useBreadcrumbs } from "../components/Breadcrumbs";
import { useIsMobile } from "../lib/useMediaQuery";
import {
  useAssignTaskMember,
  useCreateTaskNode,
  usePatchTaskNode,
  useTaskNodes,
  useUnassignTaskMember,
} from "./api/taskNodes";
import {
  type TaskNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "./api/useNotifications";
import { useTeamReminders } from "./api/useReminders";
import { CompletedPlanBanner } from "./components/CompletedPlanBanner";
import { MapSearch } from "./components/MapSearch";
import { MemberFullMap } from "./components/MemberFullMap";
import { type MyWorkItem, MyWorkView } from "./components/MyWorkView";
import { NodeDetailDrawer } from "./components/NodeDetailDrawer";
import { RemindersBell } from "./components/RemindersBell";
import { badgeLabel } from "./components/notificationText";
import { MapChangeDigest } from "./digest/MapChangeDigest";
import { useCurrentMember } from "./members/useCurrentMember";
import { useTaskTeamMembers } from "./members/useTaskTeamMembers";
import { NotificationPrefsMatrix } from "./settings/NotificationPrefsMatrix";
import { useMapTheme } from "./theme/MapThemeContext";
import { buildTree, flattenTree } from "./tree/buildTree";
import { canAuthorReason, indexById } from "./tree/canAuthor";
import { isBoardComplete, totalLeafCount } from "./tree/outcomes";
import { PALETTE } from "./tree/palette";
import type { TaskState } from "./tree/state";
import { useTreeUiStore } from "./tree/treeStore";

type MemberTab = "my-work" | "full-map" | "members";

const INDIGO = "#4F46E5";

export function MemberMapView() {
  const { teamId } = useParams<{ teamId: string }>();
  const teamQuery = useTeam(teamId);
  const teamName = teamQuery.data?.name ?? "This team";

  useBreadcrumbs(
    teamId
      ? [{ label: teamName, to: `/teams/${teamId}/map` }, { label: "Map" }]
      : [{ label: "Map" }],
  );

  // Editorial revamp (#1043, Wave 3): the member-day CONTENT surfaces below the
  // shell read the theme. The shared tab bar + 🔔 bell are app-wide chrome and
  // stay Harbour (founder shell-boundary decision) — only the content forks
  // (the loading state's warm canvas + the Members placeholder; the active
  // lenses re-skin in `MyWorkView` / `MemberFullMap`).
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // M2 / M3 (#1043, Wave 6) — on a phone in editorial, the member's node detail
  // opens as a bottom-sheet (slides up) rather than the right-side drawer; the
  // "My work" lens is already a vertical editorial column that scrolls cleanly
  // in a narrow viewport. Desktop / legacy / mobile-flag-OFF are unchanged.
  const isMobile = useIsMobile();
  const mobileEditorial = isMobile && editorial;

  const nodesQuery = useTaskNodes(teamId);
  const { list: members } = useTaskTeamMembers(teamId);
  const me = useCurrentMember(teamId);

  // B4.1 notes (#767), gated behind `task_notes` (off by default, §A10). When
  // on, the drawer's Notes tab renders the per-node feedback thread; a member
  // can add a note and remove their own (the backend enforces author/lead).
  const notesEnabled = useTeamFeature(teamId, "task_notes") === true;

  // B4.2 reminders (#768), gated behind `task_reminders` (off by default,
  // §A10). When on, the drawer's Reminders tab lets a member set a forward
  // nudge on a node they can act on (§A6) and clear ones they set; the team
  // bell is the lead editor's chrome, so members reach reminders through the
  // drawer tab here. When off the drawer is byte-unchanged.
  const remindersEnabled = useTeamFeature(teamId, "task_reminders") === true;

  // #907 — a member gets notifications too, in the SAME unified 🔔 bell as the
  // lead. Notifications are the member's OWN slice (the backend enforces this),
  // gated under `task_notes`. The bell mounts when EITHER reminders OR notes is
  // on; a cheap unread-count fetch drives the badge, the full feed loads when
  // the bell opens; the team reminder feed loads only while the bell is open.
  const notificationsEnabled = notesEnabled;
  const bellMounted = remindersEnabled || notificationsEnabled;
  const [bellOpen, setBellOpen] = useState(false);
  // B4.4 (#1043), gated behind `task_notification_prefs` (off by default). When
  // ON, the bell footer's "notification settings" link opens the per-event x
  // channel matrix (a calm overlay); each member edits their OWN matrix (§A9).
  const notificationPrefsEnabled = useTeamFeature(teamId, "task_notification_prefs") === true;
  const [notificationPrefsOpen, setNotificationPrefsOpen] = useState(false);
  const notificationBadge = useNotifications(teamId, notificationsEnabled, { full: false });
  const notificationFeed = useNotifications(teamId, bellOpen && notificationsEnabled, {
    full: true,
  });
  const teamReminders = useTeamReminders(teamId, bellOpen && remindersEnabled);
  const markRead = useMarkNotificationRead(teamId);
  const markAllRead = useMarkAllNotificationsRead(teamId);

  // Members act on nodes through the same mutation hooks the lead editor uses;
  // the backend enforces authorship (403). The UI only shows the action when
  // `canAuthor` says it's allowed, so a member's permitted edits (their own
  // subtree) flow through exactly as a lead's would.
  const patchNode = usePatchTaskNode(teamId);
  const createNode = useCreateTaskNode(teamId);
  const assignMember = useAssignTaskMember(teamId);
  const unassignMember = useUnassignTaskMember(teamId);

  const {
    selectedId,
    openNodeId,
    openTab,
    focusNoteId,
    collapsed,
    select,
    openNode,
    clearFocusNote,
    toggleCollapse,
    expand,
    reset,
  } = useTreeUiStore();

  const [tab, setTab] = useState<MemberTab>("my-work");

  // Reset transient UI state when the team changes (one global store).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on team switch only
  useEffect(() => {
    reset();
  }, [teamId, reset]);

  const flat = Array.isArray(nodesQuery.data) ? nodesQuery.data : [];
  const roots = useMemo(() => buildTree(flat), [flat]);
  const allNodes = useMemo(() => flattenTree(roots), [roots]);
  const byId = useMemo(() => indexById(roots), [roots]);

  // node id → title, so the drawer's History tab (#763) can name referenced
  // nodes ("created under '…'", "comes after '…'") from the one live source.
  const nodeTitleById = useMemo(() => new Map(flat.map((n) => [n.id, n.title])), [flat]);

  // "My work" (B5.1): the nodes assigned to ME, in tree order, with the
  // parent title as the "in: …" context. Filtering by my membership id is the
  // whole job — never a feed of others' work (§A9).
  const myItems: MyWorkItem[] = useMemo(() => {
    if (!me.membershipId) return [];
    return allNodes
      .filter((n) => n.assigneeIds.includes(me.membershipId as string))
      .map((node) => ({
        node,
        parentTitle: node.parentId ? (byId.get(node.parentId)?.title ?? null) : null,
      }));
  }, [allNodes, byId, me.membershipId]);

  const rootTitle = roots[0]?.title ?? null;

  // B8.4 (#1043) — the read-only completed-plan note for the MEMBER. When the
  // WHOLE board is complete (every outcome's leaves `done`, ≥1 task), the member
  // sees a calm done-green note that the plan is read-only for them (a lead can
  // re-open any task). It is FE-derived from the computed completion — no backend
  // enforcement; the member's edit affordances are already gated by `canAuthor`.
  // EDITORIAL-only chrome (the banner is an editorial surface).
  const boardComplete = useMemo(() => editorial && isBoardComplete(roots), [editorial, roots]);
  const leafCount = useMemo(() => totalLeafCount(roots), [roots]);

  // ── Mutation wiring (the same one-verb-each shape the lead editor uses) ──
  const setState = (id: string, state: TaskState) =>
    patchNode.mutate({ nodeId: id, patch: { state } });
  const rename = (id: string, title: string) => patchNode.mutate({ nodeId: id, patch: { title } });
  const setDescription = (id: string, description: string) =>
    patchNode.mutate({ nodeId: id, patch: { description: description.trim() || null } });
  // B2.11 (#1043) — set/clear a node's optional target date (ISO `YYYY-MM-DD`,
  // or null to clear). Gated downstream by `canAuthor` + editorial mode.
  const setTargetDate = (id: string, date: string | null) =>
    patchNode.mutate({ nodeId: id, patch: { targetDate: date } });
  const addSubtask = (parentId: string) => {
    const parent = byId.get(parentId);
    const orderIndex = parent ? parent.children.length : 0;
    createNode.mutate(
      { title: "New task", parentId, orderIndex },
      { onSuccess: () => expand(parentId) },
    );
  };
  const assignToggle = (nodeId: string, membershipId: string, assign: boolean) => {
    if (assign) assignMember.mutate({ nodeId, membershipId });
    else unassignMember.mutate({ nodeId, membershipId });
  };

  // #907 — a notification click deep-links to its note: open the node's drawer
  // on the Notes tab, scrolled to + highlighting the referenced note; mark the
  // notification read (the member's own slice) and close the bell.
  const openNotification = (notification: TaskNotification) => {
    openNode(notification.node_id, { tab: "notes", noteId: notification.note_id });
    markRead.mutate(notification.id);
    setBellOpen(false);
  };

  // ── Drawer subject + the authorship gate (B5.3) ──
  const openNodeData = openNodeId ? (byId.get(openNodeId) ?? null) : null;
  const openNodeParent = openNodeData?.parentId ? (byId.get(openNodeData.parentId) ?? null) : null;
  // §A5 (#1043 item 4) — the WHY behind a member's edit access, derived from
  // the one shared tree. `canAuthorReason` returns the granting condition (or
  // "none"); `drawerCanAuthor` is just "reason !== none" — no separate check, so
  // the gate and the explanation can never disagree.
  const drawerAuthorReason = openNodeData
    ? canAuthorReason(openNodeData, { membershipId: me.membershipId, isLead: me.isLead }, roots)
    : "none";
  const drawerCanAuthor = drawerAuthorReason !== "none";

  if (nodesQuery.isLoading) {
    return (
      <div
        data-testid="member-map-loading"
        className="flex h-[calc(100vh-8rem)] items-center justify-center"
        style={{
          backgroundColor: editorial ? c.canvas : PALETTE.canvas,
          color: editorial ? c.muted : PALETTE.muted,
          fontFamily: editorial ? theme.fonts.sans : undefined,
        }}
      >
        Loading the map…
      </div>
    );
  }

  return (
    <TeamZoneContext.Provider value={teamQuery.data?.timezone || undefined}>
      <div className="relative flex h-[calc(100vh-8rem)] flex-col" data-testid="member-map-view">
        {/* Member tab bar: My work · Full map · Members + the "you are" pill. */}
        <div
          className="flex items-center gap-6 border-b px-5 py-3"
          style={{ borderColor: PALETTE.softBorder, backgroundColor: "#FFFFFF" }}
        >
          <MemberTabButton id="my-work" active={tab} onSelect={setTab}>
            My work
          </MemberTabButton>
          <MemberTabButton id="full-map" active={tab} onSelect={setTab}>
            Full map
          </MemberTabButton>
          <MemberTabButton id="members" active={tab} onSelect={setTab}>
            Members
          </MemberTabButton>
          <div className="ml-auto flex items-center gap-3">
            {/* The unified 🔔 attention bell (#907) — a member gets notifications
              too. Mounted when either the notes or reminders layer is on; an
              unread badge shows the notification count. */}
            {bellMounted && (
              <button
                type="button"
                data-testid="reminders-bell-entry"
                onClick={() => setBellOpen(true)}
                className="relative rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-slate-50"
                style={{ borderColor: PALETTE.softBorder, color: PALETTE.slate }}
              >
                🔔
                {notificationsEnabled && badgeLabel(notificationBadge.unreadCount) && (
                  <span
                    data-testid="notifications-badge"
                    className="-top-1.5 -right-1.5 absolute inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
                    style={{ backgroundColor: PALETTE.teal }}
                  >
                    {badgeLabel(notificationBadge.unreadCount)}
                  </span>
                )}
              </button>
            )}
            {me.displayName && (
              <span
                className="flex items-center gap-2 text-[11px]"
                style={{ color: PALETTE.muted }}
              >
                you are
                <span
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold text-white"
                  style={{ backgroundColor: INDIGO }}
                  title={me.displayName}
                >
                  {me.initials ?? "?"}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* B8.4 (#1043) — the read-only completed-plan note for the member, shown
          when the WHOLE board is complete. Calm, factual, done-green; NO
          `snapshot & close` action (a lead-only verb) — the member variant just
          states the read-only fact. */}
        {boardComplete && <CompletedPlanBanner leafCount={leafCount} member />}

        {/* B3.6 "what changed since you last looked" (#765) — the member's OWN
          slice only: new tasks on their branch, state changes on their nodes,
          notes that mention them. Gated, dismissable, self-suppressing. NEVER
          a feed of others' activity (§A9). "open my work" jumps to their lens;
          a mention opens that node's drawer. */}
        <MapChangeDigest
          teamId={teamId}
          membershipId={me.membershipId}
          onOpenMyWork={() => setTab("my-work")}
          onOpenNode={openNode}
          teamTimezone={teamQuery.data?.timezone}
        />

        {/* The active lens. */}
        <div className="min-h-0 flex-1">
          {tab === "my-work" && (
            <MyWorkView
              items={myItems}
              rootTitle={rootTitle}
              onOpenNode={openNode}
              onSeeFullMap={() => setTab("full-map")}
            />
          )}
          {tab === "full-map" && (
            <MemberFullMap
              roots={roots}
              members={members}
              collapsed={collapsed}
              selectedId={selectedId}
              membershipId={me.membershipId}
              onSelect={select}
              onToggleCollapse={toggleCollapse}
              onOpenDrawer={openNode}
            />
          )}
          {tab === "members" && (
            <div
              className="flex h-full flex-col items-center justify-center px-6 text-center"
              data-testid="member-members-tab"
              style={{
                backgroundColor: editorial ? c.canvas : PALETTE.canvas,
                fontFamily: editorial ? theme.fonts.sans : undefined,
              }}
            >
              <p className="text-sm" style={{ color: editorial ? c.muted : PALETTE.muted }}>
                The team's members appear here.
              </p>
            </div>
          )}
        </div>

        {/* B5.3 — the node detail drawer over the live view, with its controls
          gated by `canAuthor` so a member only ever sees actions they're
          allowed to take (the backend remains the enforcer). */}
        {openNodeData && (
          <NodeDetailDrawer
            teamId={teamId}
            node={openNodeData}
            parent={openNodeParent}
            members={members}
            nodeTitleById={nodeTitleById}
            canAuthor={drawerCanAuthor}
            // §A5 made apparent (#1043 item 4) — the footer states WHY this member
            // can edit (assigned here / above) or, read-only, who can + how to join.
            authorReason={drawerAuthorReason}
            // B4.1 notes (#767) — a member sees the thread + can add notes; the
            // delete affordance is scoped to their own notes (a member isn't a
            // lead). The Notes tab fetches its own thread inside the drawer.
            notesEnabled={notesEnabled}
            // B4.2 reminders (#768) — a member can set a forward nudge on a node
            // and clear ones they set (the backend enforces setter/lead); off,
            // the tab is absent.
            remindersEnabled={remindersEnabled}
            currentMembershipId={me.membershipId}
            isLead={me.isLead}
            // #907 deep-link — a notification click opens this drawer on the Notes
            // tab, scrolled to + highlighting the referenced note.
            initialTab={openTab}
            focusNoteId={focusNoteId}
            onFocusConsumed={clearFocusNote}
            onClose={() => openNode(null)}
            onRename={(title) => rename(openNodeData.id, title)}
            onSetDescription={(description) => setDescription(openNodeData.id, description)}
            onSetState={(state) => setState(openNodeData.id, state)}
            onSetTargetDate={(date) => setTargetDate(openNodeData.id, date)}
            onAddSubtask={() => addSubtask(openNodeData.id)}
            onAssignToggle={(mid, assign) => assignToggle(openNodeData.id, mid, assign)}
            onOpenNode={openNode}
            // M3 (#1043, Wave 6) — sheet on a phone in editorial; right-side drawer
            // on desktop / legacy (byte-unchanged).
            presentation={mobileEditorial ? "sheet" : "drawer"}
          />
        )}

        {/* The unified attention bell (#907) over the live, dimmed view. Same
          panel the lead gets: the member's own notifications + (if on) the team
          reminder feed. A notification "open →" deep-links to its note. */}
        {bellOpen && bellMounted && (
          <>
            <button
              type="button"
              data-testid="reminders-bell-scrim"
              aria-label="Close notifications"
              onClick={() => setBellOpen(false)}
              className="absolute inset-0 z-40 cursor-default"
              style={{ backgroundColor: PALETTE.canvas, opacity: 0.45 }}
            />
            <div className="absolute right-4 top-16 z-50 max-h-[calc(100%-5rem)]">
              <RemindersBell
                notificationsEnabled={notificationsEnabled}
                notifications={notificationFeed.items}
                notificationsLoading={notificationFeed.isLoading}
                notificationsError={notificationFeed.isError}
                onOpenNotification={openNotification}
                onMarkAllRead={() => markAllRead.mutate()}
                remindersEnabled={remindersEnabled}
                reminders={teamReminders.reminders}
                isLoading={teamReminders.isLoading}
                isError={teamReminders.isError}
                nodeTitleById={nodeTitleById}
                currentMembershipId={me.membershipId}
                teamTimezone={teamQuery.data?.timezone}
                onOpenNode={(id) => {
                  openNode(id);
                  setBellOpen(false);
                }}
                onClose={() => setBellOpen(false)}
                {...(notificationPrefsEnabled
                  ? {
                      onOpenNotificationSettings: () => {
                        setBellOpen(false);
                        setNotificationPrefsOpen(true);
                      },
                    }
                  : {})}
              />
            </div>
          </>
        )}

        {/* B4.4 (#1043) — the per-event x channel notification matrix, opened from
          the bell footer's "notification settings" link (only when the layer is
          on). A calm centred overlay; the member edits their OWN matrix (§A9). */}
        {notificationPrefsEnabled && notificationPrefsOpen && (
          <div className="absolute inset-0 z-50" data-testid="notification-prefs-overlay">
            <button
              type="button"
              data-testid="notification-prefs-scrim"
              aria-label="Close notification settings"
              onClick={() => setNotificationPrefsOpen(false)}
              className="absolute inset-0 cursor-default"
              style={{ backgroundColor: PALETTE.canvas, opacity: 0.45 }}
            />
            <div className="absolute left-1/2 top-12 z-50 max-h-[calc(100%-5rem)] w-[680px] max-w-[92vw] -translate-x-1/2 overflow-auto">
              <div
                className="rounded-2xl p-6"
                style={{
                  backgroundColor: editorial ? theme.palette.canvas : "#FFFFFF",
                  border: `1px solid ${editorial ? theme.palette.rule : "#E2E8E5"}`,
                  boxShadow: "0 16px 48px rgba(26,22,18,0.18)",
                }}
              >
                <div className="mb-3 flex justify-end">
                  <button
                    type="button"
                    data-testid="notification-prefs-close"
                    aria-label="Close notification settings"
                    onClick={() => setNotificationPrefsOpen(false)}
                    className="rounded p-1 text-base leading-none transition-opacity hover:opacity-70"
                    style={{ color: theme.palette.muted }}
                  >
                    ✕
                  </button>
                </div>
                <NotificationPrefsMatrix teamId={teamId} />
              </div>
            </div>
          </div>
        )}

        {/* B2.13 (#1043, Wave 5) — the ⌘K find palette. CORE Map capability (no
          separate toggle), mounted ONLY in editorial mode so the ⌘K listener
          never attaches with the `task_editorial_ui` flag off. ↵ jumps + focuses
          (shared `select`); ⌘↵ opens the drawer (shared `openNode`). A member
          searches the whole shared map (everyone can see it — §A8). */}
        {editorial && (
          <MapSearch roots={roots} members={members} onJump={select} onOpenDrawer={openNode} />
        )}
      </div>
    </TeamZoneContext.Provider>
  );
}

function MemberTabButton({
  id,
  active,
  onSelect,
  children,
}: {
  id: MemberTab;
  active: MemberTab;
  onSelect: (id: MemberTab) => void;
  children: React.ReactNode;
}) {
  const on = id === active;
  return (
    <button
      type="button"
      data-testid={`member-tab-${id}`}
      aria-selected={on}
      onClick={() => onSelect(id)}
      className="relative pb-1 text-xs font-bold transition-colors"
      style={{ color: on ? PALETTE.teal : "#9CA3AF" }}
    >
      {children}
      {on && (
        <span
          className="absolute -bottom-[13px] left-0 h-0.5 w-full"
          style={{ backgroundColor: PALETTE.teal }}
        />
      )}
    </button>
  );
}
